/* eslint-disable no-unused-vars */
import {useToleranceCategoryMapping} from '~/composables/oring/useToleranceCategoryMapping'
import {useORingCalculatorStore} from '~/store/oring-calculator'
import {useOringCalculatorService} from '~/composables/oring_calc_service'
import {ToleranceClass} from '~/types/oring-api-responses'
import type * as OringApiRequests from '@/types/oring-api'
import useI18nExtended from '~/composables/usei18nExtended'

export interface ToleranceOption {
  text: string
  value: string | null
}

export interface ToleranceValue {
  input: number | null
  tolerance: ToleranceOption | null
  freeToleranceValue?: number | null
  freeToleranceMax?: number | null
  freeToleranceMin?: number | null
}

export interface ToleranceField {
  key: string
  value: ToleranceValue
  label: string
  minMaxText: string
  toleranceText: string
  categoryId: number
  errorMsg?: string
}

export interface ToleranceMinMaxData {
  toleranceMin: number
  toleranceMax: number
  parameterMin: number
  parameterMax: number
}

export function useToleranceDataManager() {
  const store = useORingCalculatorStore()
  const {toleranceClassesByCategory, oringDesignCockpitCurrentHousing} = storeToRefs(store)

  // Local ref to store tolerance min/max data by field key
  const toleranceMinMaxData = ref<Record<string, ToleranceMinMaxData>>({})

  // Create parameter category map from the composable
  const {createParameterCategoryMap} = useToleranceCategoryMapping()
  const parameterCategoryMap = createParameterCategoryMap()

  // Map from store parameter keys to tolerance category mapping keys
  const keyMappingToTolerance: Record<string, string> = {
    'd1': 'd1',
    'd2': 'd2',
    'd3': 'd3',
    'd4': 'd4',
    'd5': 'd5',
    'd6': 'd6',
    'd8': 'd8',
    'd9': 'd9',
    'd10': 'd10',
    'r1': 'r1',
    'b1in': 'b1',
    'b1out': 'b1',
    'b4': 'b4',
    'gap': 'g',
    'height': 'h'
  }

  // Helper function to get the correct tolerance mapping key based on geometry
  const getToleranceMappingKey = (key: string): string => {
    // Special case for d3: use d3Groove when geometry is 9 (groove in shaft)
    if (key === 'd3' && store.oringDesignCockpitGeometryId === 9) {
      return 'd3Groove'
    }
    return keyMappingToTolerance[key] || key
  }

  // Translation key mapping for labels - using actual parameter translation keys
  const labelTranslationMap: Record<string, string> = {
    'd1': 'Frontend.Ring.D1',
    'd2': 'Frontend.Ring.D2',
    'r1': 'Frontend.Housing.R1',
    'd3': 'Frontend.Housing.D3',
    'd4': 'Frontend.Housing.D4',
    'd5': 'Frontend.Housing.D5',
    'd6': 'Frontend.Housing.D6',
    'd8': 'Frontend.Housing.D8',
    'd9': 'Frontend.Housing.D9',
    'd10': 'Frontend.Housing.D10',
    'b1in': 'Frontend.Housing.B1',
    'b1out': 'Frontend.Housing.B1',
    'b4': 'Frontend.Housing.B4',
    'gap': 'Frontend.Housing.Gap',
    'height': 'Frontend.Housing.Height'
  }

  // Initialize tolerance classes on mount
  async function initializeToleranceClasses() {
    const oringService = useOringCalculatorService()

    // Get preloads which includes tolerance classes with all their details
    const preloadsResponse = await oringService.getPreloadsExpertMode()

    if (preloadsResponse?.toleranceClasses) {
      // Use the new store function to set tolerance classes and automatically
      // categorize them by toleranceCategoryId
      store.toleranceClasses = preloadsResponse.toleranceClasses
      store.buildToleranceClassesByCategory() // Build the Map after setting toleranceClasses
    }
    if (preloadsResponse?.defaultTolerances) {
      store.oringDesignCockpitDefaultTolerances = preloadsResponse.defaultTolerances
    }
  }

  // General function to get tolerance options by category
  function getToleranceOptionsByCategory(classCategoryId: number): ToleranceOption[] {
    if (!toleranceClassesByCategory.value) {
      return []
    }

    const item = toleranceClassesByCategory.value.get(classCategoryId)
    if (item && item.length > 0) {
      return item.map((option: ToleranceClass) => ({
        text: option.toleranceClassName,
        value: option.toleranceClassId !== null ? String(option.toleranceClassId) : null
      }))
    }
    return []
  }

  // Helper to get ToleranceOption by id and category
  function getToleranceOptionById(categoryId: number, id: string | null): ToleranceOption | null {
    if (!id) {
      return null
    }
    const options = getToleranceOptionsByCategory(categoryId)
    return options.find(opt => opt.value === String(id)) || null
  }

  // Get current parameter value from housing
  function getParameterValue(key: string): number | null {
    if (!oringDesignCockpitCurrentHousing.value) {
      return null
    }

    // Navigate through the housing object to get the parameter value
    const housing = oringDesignCockpitCurrentHousing.value
    switch (key) {
    case 'd1': return housing.oRing?.d1?.parameterValue ?? null
    case 'd2': return housing.oRing?.d2?.parameterValue ?? null
    case 'r1': return housing.r1?.parameterValue ?? null
    case 'd3': return housing.d3?.parameterValue ?? null
    case 'd4': return housing.d4?.parameterValue ?? null
    case 'd5': return housing.d5?.parameterValue ?? null
    case 'd6': return housing.d6?.parameterValue ?? null
    case 'd8': return housing.d8?.parameterValue ?? null
    case 'd9': return housing.d9?.parameterValue ?? null
    case 'd10': return housing.d10?.parameterValue ?? null
    case 'b1in':
    case 'b1out': return housing.b1?.parameterValue ?? null
    case 'b4': return housing.b4?.parameterValue ?? null
    case 'gap': return housing.gapWidth?.parameterValue ?? null
    case 'height': return housing.h?.parameterValue ?? null
    default: return null
    }
  }

  // Get tolerance class ID from housing only (separate from getCurrentToleranceClassId)
  function getHousingToleranceClassId(key: string): string | null {
    if (!oringDesignCockpitCurrentHousing.value) {
      return null
    }

    const housing = oringDesignCockpitCurrentHousing.value
    switch (key) {
    case 'd1': return housing.oRing?.d1?.toleranceClassId?.toString() ?? null
    case 'd2': return housing.oRing?.d2?.toleranceClassId?.toString() ?? null
    case 'r1': return housing.r1?.toleranceClassId?.toString() ?? null
    case 'd3': return housing.d3?.toleranceClassId?.toString() ?? null
    case 'd4': return housing.d4?.toleranceClassId?.toString() ?? null
    case 'd5': return housing.d5?.toleranceClassId?.toString() ?? null
    case 'd6': return housing.d6?.toleranceClassId?.toString() ?? null
    case 'd8': return housing.d8?.toleranceClassId?.toString() ?? null
    case 'd9': return housing.d9?.toleranceClassId?.toString() ?? null
    case 'd10': return housing.d10?.toleranceClassId?.toString() ?? null
    case 'b1in':
    case 'b1out': return housing.b1?.toleranceClassId?.toString() ?? null
    case 'b4': return housing.b4?.toleranceClassId?.toString() ?? null
    case 'gap': return housing.gapWidth?.toleranceClassId?.toString() ?? null
    case 'height': return housing.h?.toleranceClassId?.toString() ?? null
    default: return null
    }
  }

  // Get current tolerance class ID from housing or preference data
  function getCurrentToleranceClassId(key: string, toleranceData: Record<string, string | null>): string | null {
    // First check if we have a value in the tolerance data (user's changes)
    if (toleranceData && toleranceData[key]) {
      return toleranceData[key]
    }

    // Fall back to housing values if no model value exists
    return getHousingToleranceClassId(key)
  }

  // Initialize tolerance data from housing values if toleranceData is empty
  function initializeToleranceDataFromHousing(
    toleranceData: Record<string, string | null>,
    emitToleranceData: (data: Record<string, string | null>) => void,
    emitParameterData: (data: Record<string, number | null>) => void
  ) {
    if (!oringDesignCockpitCurrentHousing.value) {
      return
    }

    // Only initialize if toleranceData is empty or has no values
    const hasExistingToleranceValues = Object.keys(toleranceData).some(key => toleranceData[key] !== null)
    const hasExistingParameterValues = Object.keys(store.parameterInputData || {}).some(key =>
      store.parameterInputData?.[key] !== null && store.parameterInputData?.[key] !== undefined
    )

    // Don't override if we already have user data in the store
    if (hasExistingToleranceValues || hasExistingParameterValues) {
      return // Don't override existing user selections
    }

    const toleranceParams = store.oringDesignCockpitToleranceParameters()
    const initialToleranceData: Record<string, string | null> = {}
    const initialParameterData: Record<string, number | null> = {}

    toleranceParams.forEach((param: OringApiRequests.ORingToleranceParameter) => {
      const key = param.toleranceParameterKey
      const housingToleranceId = getHousingToleranceClassId(key)
      const housingParameterValue = getParameterValue(key)

      if (housingToleranceId) {
        initialToleranceData[key] = housingToleranceId
      }
      if (housingParameterValue !== null) {
        initialParameterData[key] = housingParameterValue
      }
    })

    if (Object.keys(initialToleranceData).length > 0) {
      emitToleranceData(initialToleranceData)
    }
    if (Object.keys(initialParameterData).length > 0) {
      emitParameterData(initialParameterData)
    }
  }

  // Call the getTolerancesExpertMode API to get tolerance min/max values
  // Optional expertHousing parameter allows using Assistant housing instead of Design Cockpit housing
  async function callGetTolerancesAPI(key: string, value: number, toleranceClassId: number, expertHousing?: any): Promise<void> {
    try {
      const oringService = useOringCalculatorService()

      const housingToUse = expertHousing || oringDesignCockpitCurrentHousing.value
      if (!housingToUse) {
        return
      }

      const request: OringApiRequests.ORingExpertModeGetTolerancesRequest = {
        value: value,
        toleranceClassId: toleranceClassId,
        propertyName: key,
        language: useNuxtApp().$i18n.locale.value,
        imperial: store.oringCalculatorImperial,
        expertInputHousing: housingToUse
      }

      const result = await oringService.getTolerancesExpertMode(request)

      // Store the tolerance data for this field
      if (result.data) {
        toleranceMinMaxData.value[key] = {
          toleranceMin: result.data.toleranceMin,
          toleranceMax: result.data.toleranceMax,
          parameterMin: result.data.parameterMin,
          parameterMax: result.data.parameterMax
        }
      }

      if (result.housing) {
        store.oringDesignCockpitCurrentHousing = result.housing
      }

    } catch {
      // Silently handle API errors to prevent breaking the UI
      // Tolerance data will fall back to default values
    }
  }

  // Timer storage for debounced API calls
  const toleranceTimers = new Map<string, ReturnType<typeof setTimeout>>()

  // Call tolerance API for a single field with debouncing (like ToleranceChecker does)
  function callGetTolerancesForField(
    key: string,
    parameterValue: number,
    toleranceClassId: string,
    expertHousing?: any,
    debounceMs: number = 500
  ): void {
    // Clear existing timer for this field if it exists
    const existingTimer = toleranceTimers.get(key)
    if (existingTimer) {
      clearTimeout(existingTimer)
    }

    // Set new debounced timer for this field
    const timer = setTimeout(async () => {
      try {
        await callGetTolerancesAPI(key, parameterValue, parseInt(toleranceClassId), expertHousing)
      } catch {
        // Silently handle API errors for debounced calls
      }
      // Clean up timer reference
      toleranceTimers.delete(key)
    }, debounceMs)

    toleranceTimers.set(key, timer)
  }

  // Initialize tolerance min/max data for existing parameter and tolerance values
  async function initializeToleranceMinMaxData(
    toleranceData: Record<string, string | null>,
    parameterInputData: Record<string, number | null>
  ) {
    const toleranceParams = store.oringDesignCockpitToleranceParameters()
    for (const param of toleranceParams) {
      const key = param.toleranceParameterKey
      const parameterValue = parameterInputData[key] ?? getParameterValue(key)
      const toleranceClassId = toleranceData[key] ?? getCurrentToleranceClassId(key, toleranceData)

      if (parameterValue !== null && toleranceClassId) {
        try {
          await callGetTolerancesAPI(key, parameterValue, parseInt(toleranceClassId))
        } catch {
          // Continue with other parameters if one fails
        }
      }
    }
  }

  // Generate tolerance fields based on current geometry
  function generateToleranceFields(
    toleranceData: Record<string, string | null>,
    parameterInputData: Record<string, number | null>,
    getParameterMinMaxText: (key: string) => string,
    getToleranceMinMaxText: (key: string) => string,
    i18n: ReturnType<typeof useI18nExtended>
  ): ToleranceField[] {
    // Get tolerance parameters from store function
    const toleranceParams = store.oringDesignCockpitToleranceParameters()

    const fields = toleranceParams.map((param: OringApiRequests.ORingToleranceParameter): ToleranceField => {
      const key = param.toleranceParameterKey
      const toleranceKey = getToleranceMappingKey(key)
      const categoryId = parameterCategoryMap[toleranceKey] || 1
      const labelTranslationKey = labelTranslationMap[key] || `Frontend.Parameter.${key}`
      let currentToleranceId = getCurrentToleranceClassId(key, toleranceData)

      if (!currentToleranceId) {
        const defaultTolerance = store.oringDesignCockpitDefaultTolerances?.find(t => t.parameterTranslationKey === toleranceKey)
        currentToleranceId = defaultTolerance?.defaultToleranceClassId?.toString() || null
      }

      // Use parameter input data if available, otherwise fall back to housing value
      // But only fall back if the parameter has never been explicitly set (not in parameterInputData object)
      const inputValue = key in parameterInputData
        ? parameterInputData[key]
        : getParameterValue(key)

      const persistedFree = store.freeToleranceValues ? store.freeToleranceValues[key] || {} : {}

      return {
        key,
        value: {
          input: inputValue,
          tolerance: getToleranceOptionById(categoryId, currentToleranceId),
          freeToleranceValue: persistedFree.value ?? null,
          freeToleranceMin: persistedFree.min ?? null,
          freeToleranceMax: persistedFree.max ?? null
        },
        label: i18n.tv(labelTranslationKey, 'ORingConfiguratorImport'),
        minMaxText: getParameterMinMaxText(key),
        toleranceText: getToleranceMinMaxText(key),
        categoryId
      }
    })

    return fields
  }

  // Apply default tolerances based on current geometry and default tolerance settings
  function applyDefaultTolerances(
    emitToleranceData: (data: Record<string, string | null>) => void
  ) {
    if (!store.oringDesignCockpitDefaultTolerances) {
      return
    }

    const toleranceParams = store.oringDesignCockpitToleranceParameters()
    const defaultToleranceData: Record<string, string | null> = {}

    toleranceParams.forEach((param: OringApiRequests.ORingToleranceParameter) => {
      const key = param.toleranceParameterKey
      const toleranceKey = getToleranceMappingKey(key)
      const categoryId = parameterCategoryMap[toleranceKey] || 1

      // Find matching default tolerance based on parameter key, geometry, and category
      const defaultTolerance = store.oringDesignCockpitDefaultTolerances?.find(
        t => t.parameterTranslationKey === toleranceKey &&
             t.geometryId === store.oringDesignCockpitGeometryId &&
             t.toleranceCategoryId === categoryId
      )

      if (defaultTolerance?.defaultToleranceClassId) {
        defaultToleranceData[key] = defaultTolerance.defaultToleranceClassId.toString()
      }
      if (toleranceKey === 'd3Groove' && store.oringDesignCockpitGeometryId === 9) {
        // Special case for d3Groove, should be defaulted to a11 like it is in old ORC, see: task #41727
        defaultToleranceData[key] = toleranceClassesByCategory.value.get(categoryId)?.find(
          (toleranceClass: ToleranceClass) => toleranceClass.toleranceClassName === 'a11'
        )?.toleranceClassId?.toString() ?? defaultTolerance?.defaultToleranceClassId?.toString()
      }
    })

    if (Object.keys(defaultToleranceData).length > 0) {
      emitToleranceData(defaultToleranceData)
    }
  }

  function setToleranceMinMaxData(key: string, data: ToleranceMinMaxData) {
    toleranceMinMaxData.value[key] = data
  }

  async function callCheckFreeToleranceAPI(
    key: string,
    parameterValue: number,
    toleranceClassId: string,
    freeToleranceValue: number | null | undefined,
    freeToleranceMax: number | null | undefined,
    freeToleranceMin: number | null | undefined,
    expertHousing?: any
  ): Promise<void> {
    const housing = expertHousing || store.oringDesignCockpitCurrentHousing
    if (!housing) {
      return
    }

    const toleranceClass = store.toleranceClasses.find(tc => tc.toleranceClassId.toString() === toleranceClassId)
    if (!toleranceClass) {
      return
    }

    const className = toleranceClass.toleranceClassName.toLowerCase()
    const isFreeSym = className.includes('free') && className.includes('sym') && !className.includes('asym')
    const isFreeAsym = className.includes('free') && className.includes('asym')

    if (!isFreeSym && !isFreeAsym) {
      return
    }

    let toleranceMax: number
    let toleranceMin: number
    let isEqual: boolean

    if (isFreeSym) {
      if (freeToleranceValue === null || freeToleranceValue === undefined) {
        return
      }

      toleranceMax = freeToleranceValue
      toleranceMin = -freeToleranceValue
      isEqual = true
    } else {
      if (freeToleranceMax === null || freeToleranceMax === undefined || freeToleranceMin === null || freeToleranceMin === undefined) {
        return
      }
      toleranceMax = freeToleranceMax
      toleranceMin = freeToleranceMin
      isEqual = false
    }

    const toleranceInfo: OringApiRequests.ORingToleranceParameter = {
      toleranceParameterKey: key,
      toleranceInfoString: JSON.stringify({
        ParameterValue: parameterValue.toString(),
        ToleranceClassId: toleranceClassId,
        ToleranceMin: toleranceMin.toString(),
        ToleranceMax: toleranceMax.toString(),
        IsEqual: isEqual.toString()
      })
    }

    try {
      const oringService = useOringCalculatorService()
      const result = await oringService.checkFreeToleranceExpertMode({
        language: useNuxtApp().$i18n.locale.value,
        imperial: store.oringCalculatorImperial,
        freeTolerance: toleranceInfo,
        housing: housing
      })

      if (result.data) {
        setToleranceMinMaxData(key, {
          toleranceMin: result.data.toleranceMin,
          toleranceMax: result.data.toleranceMax,
          parameterMin: result.data.parameterMin,
          parameterMax: result.data.parameterMax
        })
      }

      if (result?.housing) {
        store.oringDesignCockpitCurrentHousing = result.housing
      }
    } catch {
      // Silent error handling
    }
  }

  return {
    toleranceMinMaxData: readonly(toleranceMinMaxData),
    initializeToleranceClasses,
    getToleranceOptionsByCategory,
    getToleranceOptionById,
    getParameterValue,
    getHousingToleranceClassId,
    getCurrentToleranceClassId,
    initializeToleranceDataFromHousing,
    callGetTolerancesAPI,
    callGetTolerancesForField,
    initializeToleranceMinMaxData,
    generateToleranceFields,
    applyDefaultTolerances,
    setToleranceMinMaxData,
    callCheckFreeToleranceAPI
  }
}
